import { useEffect, useCallback, useRef } from 'react';

interface UseCleanupHandlersProps {
  isGeneratingResponse: boolean;
  filteredMessages: any[];
  handleStopStreaming: () => void;
}

export const useCleanupHandlers = ({
  isGeneratingResponse,
  filteredMessages,
  handleStopStreaming
}: UseCleanupHandlersProps) => {
  const isUnmountingRef = useRef(false);
  const stableStopStreamingRef = useRef(handleStopStreaming);
  
  // Keep ref updated but don't trigger effects
  stableStopStreamingRef.current = handleStopStreaming;

  const handleBeforeUnload = useCallback(() => {
    if (isGeneratingResponse && filteredMessages.length > 0) {
      try {
        stableStopStreamingRef.current();
      } catch (error) {
        // Ignore AbortError on page unload
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error('Error during page unload cleanup:', error);
        }
      }
    }
  }, [isGeneratingResponse, filteredMessages]);

  useEffect(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleBeforeUnload]);

  // Only cleanup on actual component unmount
  useEffect(() => {
    return () => {
      isUnmountingRef.current = true;
    };
  }, []); // Empty deps - only runs on mount/unmount
  
  // Separate effect for cleanup that doesn't re-run on dependency changes
  useEffect(() => {
    const cleanup = () => {
      if (isUnmountingRef.current) {
        try {
          stableStopStreamingRef.current();
        } catch (error) {
          // Ignore AbortError on unmount as it's expected
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error during cleanup:', error);
          }
        }
      }
    };
    
    // Only register cleanup if actually generating
    if (isGeneratingResponse) {
      window.addEventListener('beforeunload', cleanup);
      return () => {
        window.removeEventListener('beforeunload', cleanup);
        cleanup();
      };
    }
    
    // Return empty cleanup function when not generating
    return () => {};
  }, [isGeneratingResponse]); // Only re-run when generation state changes

  return { handleBeforeUnload };
};